/*
 * WebAuthn's browser plumbing.
 *
 * THE WHOLE PROBLEM THIS SOLVES is that WebAuthn's JavaScript API speaks
 * ArrayBuffer and its wire format speaks base64url. `navigator.credentials` will
 * not accept a challenge as a string, and JSON cannot carry an ArrayBuffer, so
 * every field the server issues has to be decoded on the way in and every field
 * the authenticator produces has to be encoded on the way out. Getting one of
 * them wrong produces a signature the server rejects with no useful diagnostic,
 * which is why the conversions live in one audited file rather than inline.
 *
 * WHY NOT PublicKeyCredential.parseCreationOptionsFromJSON(). It does exactly
 * this and is the right answer eventually, but it landed in Chrome 119 and Safari
 * 17.4 — and the device that matters most here is a parent's phone at a venue,
 * which is precisely the population that has not updated. The conversions below
 * work everywhere WebAuthn does.
 *
 * BASE64URL, NOT BASE64: the alphabet substitutes `-` for `+` and `_` for `/`,
 * and drops the `=` padding. Feeding a base64url string to `atob()` fails or, on
 * some inputs, silently produces different bytes — which would present as an
 * intermittently invalid signature.
 */

function base64urlToBuffer(value: string): ArrayBuffer {
  // Padding restored before decoding: atob() rejects an unpadded string in some
  // engines and accepts it in others, and an intermittent failure across browsers
  // is worse than a consistent one.
  const padded = value.padEnd(value.length + ((4 - (value.length % 4)) % 4), "=");
  const binary = atob(padded.replace(/-/g, "+").replace(/_/g, "/"));

  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

function bufferToBase64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  // Chunked rather than String.fromCharCode(...bytes): a spread over a large
  // attestation object overflows the argument limit and throws.
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** Whether this browser can do WebAuthn at all. */
export function passkeysSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.PublicKeyCredential !== "undefined" &&
    typeof navigator.credentials?.create === "function"
  );
}

/*
 * `transports` is the one field needing a cast, and it is worth a note.
 *
 * The wire carries it as string[] — it is JSON — while the DOM types it as a
 * closed union (AuthenticatorTransport: "usb" | "nfc" | "ble" | "internal" |
 * "hybrid"). The values come from our own server, which got them from a previous
 * ceremony, so they are already valid; and a transport the browser does not
 * recognise is IGNORED by the platform rather than fatal, which is why widening
 * here is safe. Narrowing by filtering against a hard-coded list would be worse:
 * it would silently drop a transport added to the spec after this was written.
 */
function descriptors(
  raw: { id: string; type: string; transports?: string[] }[] | undefined,
  decode: (v: string) => ArrayBuffer,
): PublicKeyCredentialDescriptor[] | undefined {
  if (!raw) return undefined;
  return raw.map((c) => ({
    id: decode(c.id),
    type: "public-key" as const,
    ...(c.transports ? { transports: c.transports as AuthenticatorTransport[] } : {}),
  }));
}

/*
 * The server's options, as JSON. Typed loosely on purpose: this is a
 * pass-through of a spec-defined structure that go-webauthn owns, and restating
 * it here would create a second definition to keep in step for no benefit. The
 * fields touched below are the ones that must be decoded, and they are named
 * explicitly so a missing one is a type error rather than a runtime surprise.
 */
interface CreationOptionsJSON {
  challenge: string;
  user: { id: string; name: string; displayName: string };
  excludeCredentials?: { id: string; type: string; transports?: string[] }[];
  [key: string]: unknown;
}

interface RequestOptionsJSON {
  challenge: string;
  allowCredentials?: { id: string; type: string; transports?: string[] }[];
  [key: string]: unknown;
}

/** Registers a new passkey, returning the credential as JSON for the server. */
export async function createPasskey(optionsJSON: unknown): Promise<unknown> {
  const o = optionsJSON as CreationOptionsJSON;

  const publicKey: PublicKeyCredentialCreationOptions = {
    ...(o as unknown as PublicKeyCredentialCreationOptions),
    challenge: base64urlToBuffer(o.challenge),
    user: {
      ...o.user,
      // The user handle is the ACCOUNT UUID, never the email (see passkey.go):
      // an email can change, and a handle that changed would orphan every
      // credential registered under the old one.
      id: base64urlToBuffer(o.user.id),
    },
    ...(o.excludeCredentials
      ? { excludeCredentials: descriptors(o.excludeCredentials, base64urlToBuffer) }
      : {}),
  };

  const credential = await navigator.credentials.create({ publicKey });
  if (!credential) throw new Error("no credential was created");

  const pk = credential as PublicKeyCredential;
  const response = pk.response as AuthenticatorAttestationResponse;

  return {
    id: pk.id,
    rawId: bufferToBase64url(pk.rawId),
    type: pk.type,
    // Extension results travel back because go-webauthn reads them; an empty
    // object is the correct value when there are none, not an omission.
    clientExtensionResults: pk.getClientExtensionResults(),
    response: {
      clientDataJSON: bufferToBase64url(response.clientDataJSON),
      attestationObject: bufferToBase64url(response.attestationObject),
    },
  };
}

/**
 * Authenticates with a passkey.
 *
 * DISCOVERABLE LOGIN: no allowCredentials is sent for the usual case, so the
 * authenticator offers whatever it holds for this site and the user never types
 * an email first. That is what makes the passkey button a genuinely one-tap
 * sign-in — and it is why the server registers resident keys as Required
 * (passkey.go). A flow that asked for the email first would be a "split flow" of
 * exactly the kind §12's acceptance criterion rules out.
 */
export async function getPasskeyAssertion(optionsJSON: unknown): Promise<unknown> {
  const o = optionsJSON as RequestOptionsJSON;

  const publicKey: PublicKeyCredentialRequestOptions = {
    ...(o as unknown as PublicKeyCredentialRequestOptions),
    challenge: base64urlToBuffer(o.challenge),
    ...(o.allowCredentials
      ? { allowCredentials: descriptors(o.allowCredentials, base64urlToBuffer) }
      : {}),
  };

  const credential = await navigator.credentials.get({ publicKey });
  if (!credential) throw new Error("no credential was returned");

  const pk = credential as PublicKeyCredential;
  const response = pk.response as AuthenticatorAssertionResponse;

  return {
    id: pk.id,
    rawId: bufferToBase64url(pk.rawId),
    type: pk.type,
    clientExtensionResults: pk.getClientExtensionResults(),
    response: {
      clientDataJSON: bufferToBase64url(response.clientDataJSON),
      authenticatorData: bufferToBase64url(response.authenticatorData),
      signature: bufferToBase64url(response.signature),
      // The user handle is how the server finds the account in a discoverable
      // login. Null when the authenticator did not return one, which is a real
      // case for older keys — and the reason the server also accepts the
      // credential id as the lookup.
      userHandle: response.userHandle ? bufferToBase64url(response.userHandle) : null,
    },
  };
}

/**
 * Whether a WebAuthn failure was the user changing their mind.
 *
 * A cancelled ceremony throws `NotAllowedError`, the same error a genuine
 * failure throws. Telling them apart matters for the copy: "that didn't work"
 * after someone deliberately dismissed the system sheet is the app blaming a user
 * for a decision, which §4's voice rules rule out.
 */
export function wasCancelled(err: unknown): boolean {
  return err instanceof DOMException && (err.name === "NotAllowedError" || err.name === "AbortError");
}
